import React, { useEffect, useState } from "react";
import { Plus, Mail, Archive, ArchiveRestore, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { formatApiError } from "../lib/errors";
import { ukDate } from "../lib/format";
import { PageHeader, Card, EmptyState, PrimaryButton, QuietButton } from "../components/UI";
import { EmailTemplateDialog } from "../components/email/EmailTemplateDialog";

/** LT-38 — Libraries → Email Templates. Request-for-quote wording with {merge_fields}; used by Tender → Output → Send email. */
export default function EmailTemplates() {
  const [items, setItems] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [dialog, setDialog] = useState({ open: false, initial: null });

  const load = () => {
    setLoading(true);
    api.get("/email-templates", { params: { include_archived: showArchived || undefined } })
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e, "Failed to load email templates")))
      .finally(() => setLoading(false));
  };
  useEffect(load, [showArchived]); // eslint-disable-line
  useEffect(() => { api.get("/email-templates/merge-fields").then((r) => setFields(r.data.fields || [])).catch(() => {}); }, []);

  const toggle = async (t) => {
    try {
      await api.post(`/email-templates/${t.id}/${t.status === "archived" ? "activate" : "archive"}`);
      toast.success(t.status === "archived" ? "Template restored" : "Template archived");
      load();
    } catch (e) { toast.error(formatApiError(e, "Could not change status")); }
  };

  return (
    <div className="app-container py-8" data-testid="email-templates-page">
      <PageHeader title="Email Templates" subtitle="Request-for-quote wording. Pick one on a tender under Output → Send email; the fields fill in and the message opens in Outlook."
        actions={<PrimaryButton icon={Plus} onClick={() => setDialog({ open: true, initial: null })} data-testid="email-templates-add">New template</PrimaryButton>} />
      <div className="flex items-center justify-between mb-3">
        <div className="flex flex-wrap gap-1.5" data-testid="email-templates-fields">
          <span className="text-ink-3 mr-1" style={{ fontSize: 11.5 }}>Merge fields:</span>
          {fields.map((f) => <span key={f.key} className="rounded-full px-2 py-0.5 bg-surface-2 text-ink-2 border border-line font-mono" style={{ fontSize: 10.5 }} title={f.label}>{`{${f.key}}`}</span>)}
        </div>
        <label className="inline-flex items-center gap-1.5 text-ink-3" style={{ fontSize: 12 }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="email-templates-show-archived" /> Show archived
        </label>
      </div>
      <Card>
        {loading ? <div className="py-12 text-center text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
        : items.length === 0 ? <EmptyState icon={Mail} title="No email templates" hint="Create one to send quote requests from a tender." action={<PrimaryButton icon={Plus} onClick={() => setDialog({ open: true, initial: null })}>New template</PrimaryButton>} />
        : (
          <table className="w-full text-left" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <thead><tr>
              <th className="col-head px-4 py-3" style={{ borderBottom: "1px solid #E2DDD2" }}>Template</th>
              <th className="col-head px-4 py-3" style={{ borderBottom: "1px solid #E2DDD2" }}>Subject</th>
              <th className="col-head px-4 py-3" style={{ width: 120, borderBottom: "1px solid #E2DDD2" }}>Updated</th>
              <th style={{ width: 170, borderBottom: "1px solid #E2DDD2" }} />
            </tr></thead>
            <tbody>
              {items.map((t, i) => (
                <tr key={t.id} data-testid={`email-template-row-${t.id}`} className={["hover:bg-surface-2/60", i !== items.length - 1 ? "border-b border-line-soft" : "", t.status === "archived" ? "opacity-60" : ""].join(" ")}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }} data-testid={`email-template-name-${t.id}`}>{t.name}</span>
                      {t.status === "archived" && <span className="rounded font-mono" style={{ background: "#F0EEE6", color: "#7A6238", border: "1px solid #E2DDD2", padding: "1px 5px", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em" }}>ARCHIVED</span>}
                    </div>
                    <div className="text-ink-3 mt-0.5 truncate" style={{ fontSize: 11, maxWidth: 360 }}>{(t.body || "").split("\n")[0]}</div>
                  </td>
                  <td className="px-4 py-3 text-ink-2 font-mono" style={{ fontSize: 11.5 }}>{t.subject || "—"}</td>
                  <td className="px-4 py-3 text-ink-3" style={{ fontSize: 11.5 }}>{ukDate(t.updated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <QuietButton icon={Pencil} onClick={() => setDialog({ open: true, initial: t })} data-testid={`email-template-edit-${t.id}`}>Edit</QuietButton>
                      <QuietButton icon={t.status === "archived" ? ArchiveRestore : Archive} onClick={() => toggle(t)} data-testid={`email-template-archive-${t.id}`}>{t.status === "archived" ? "Restore" : "Archive"}</QuietButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      <EmailTemplateDialog open={dialog.open} initial={dialog.initial} fields={fields} onClose={() => setDialog({ open: false, initial: null })} onSaved={load} />
    </div>
  );
}
